// src/components/Admin/BoardCategoryManager/CategoryForm.jsx
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, FormLabel,
  RadioGroup, FormControlLabel, Radio, MenuItem,
} from '@mui/material';
import {
  createCategory,
  editCategory,
  getBoardGroups,      // ★ 그룹 목록 API
} from '../../../api/admin';
import { useCategory } from '../../../contexts/CategoryContext';

/* ───────── 기본값 ───────── */
const defaultForm = {
  name: '',
  description: '',
  anonymous_option: 'none',
  secret_option: 'none',
  emoji: '📁',
  write_permission: 'all',
  read_cost: 0,
  group_id: '',        // ★ 새 필드
};

const CategoryForm = ({ open, onClose, category, isEdit, reload }) => {
  const [form, setForm] = useState(defaultForm);
  const [groups, setGroups] = useState([]);
  const { reloadCategories } = useCategory();

  /* ── 그룹 목록 Load ─────────────────── */
  useEffect(() => {
    if (open) {
      getBoardGroups()
        .then((res) => setGroups(res.data.groups ?? []))
        .catch(console.error);
    }
  }, [open]);

  /* ── 폼 초기화 ──────────────────────── */
  useEffect(() => {
    if (isEdit && category) {
      setForm({
        ...defaultForm,
        ...category,
        emoji: category.emoji || '📁',
        group_id: category.group?.id ?? '', // 편집 시 그룹 지정
      });
    } else {
      setForm(defaultForm);
    }
  }, [category, isEdit, open]);

  /* ── 입력 핸들러 ────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    const parsed =
      name === 'read_cost' ? Number(value) : value; // 숫자 변환
    setForm((prev) => ({ ...prev, [name]: parsed }));
  };

  /* ── 저장 ──────────────────────────── */
  const handleSubmit = () => {
    if (!form.name.trim()) {
      alert('게시판명을 입력하세요.');
      return;
    }
    if (!form.group_id) {
      alert('게시판 그룹을 선택하세요.');
      return;
    }

    const payload = { ...form, is_active: true };

    const apiCall = isEdit
      ? editCategory(form.id, payload)
      : createCategory(payload);

    apiCall
      .then(() => {
        reload();
        reloadCategories();
        onClose();
      })
      .catch(() => alert('저장 실패'));
  };

  /* ── UI ────────────────────────────── */
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiDialog-paper': { '@media (max-width:600px)': { margin: 1 } } }}
    >
      <DialogTitle>{isEdit ? '게시판 수정' : '게시판 추가'}</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mt: 1,
          '@media (max-width:600px)': { gap: 1.5 },
        }}
      >
        {/* ── 게시판명 ── */}
        <TextField
          name="name"
          label="게시판명"
          value={form.name}
          onChange={handleChange}
          fullWidth
          required
        />

        {/* ── 설명 ── */}
        <TextField
          name="description"
          label="설명"
          value={form.description}
          onChange={handleChange}
          fullWidth
          multiline
          rows={3}
        />

        {/* ── 그룹 선택 ── */}
        <TextField
          name="group_id"
          label="게시판 그룹"
          select
          fullWidth
          required
          value={form.group_id}
          onChange={handleChange}
        >
          {groups.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.emoji} {g.name}
            </MenuItem>
          ))}
        </TextField>

        {/* ── 익명 옵션 ── */}
        <FormControl>
          <FormLabel>글 작성 방식</FormLabel>
          <RadioGroup
            name="anonymous_option"
            value={form.anonymous_option}
            onChange={handleChange}
            row
            sx={{ '@media (max-width:600px)': { flexDirection: 'column' } }}
          >
            <FormControlLabel value="none" control={<Radio />} label="공개" />
            <FormControlLabel value="force" control={<Radio />} label="익명" />
          </RadioGroup>
        </FormControl>

        {/* ── 비밀글 옵션 ── */}
        <FormControl>
          <FormLabel>글 공개 여부</FormLabel>
          <RadioGroup
            name="secret_option"
            value={form.secret_option}
            onChange={handleChange}
            row
            sx={{ '@media (max-width:600px)': { flexDirection: 'column' } }}
          >
            <FormControlLabel value="none" control={<Radio />} label="공개글" />
            <FormControlLabel value="force" control={<Radio />} label="비밀글" />
          </RadioGroup>
        </FormControl>

        {/* ── 이모지 ── */}
        <TextField
          name="emoji"
          label="이모지 (예: 💬 👤 📢 🔒)"
          value={form.emoji || ''}
          onChange={handleChange}
          fullWidth
          inputProps={{ maxLength: 2 }}
          placeholder="예: 💬 👤 📢 🔒 📁"
        />

        {/* ── 작성 권한 ── */}
        <TextField
          name="write_permission"
          label="작성 권한"
          value={form.write_permission}
          onChange={handleChange}
          select
          fullWidth
        >
          <MenuItem value="all">전체 작성 가능</MenuItem>
          <MenuItem value="admin">어드민 이상만 작성</MenuItem>
          <MenuItem value="master">마스터만 작성</MenuItem>
        </TextField>

        {/* ── 읽기 비용 ── */}
        <TextField
          name="read_cost"
          label="읽기 비용(포인트)"
          type="number"
          inputProps={{ min: 0 }}
          value={form.read_cost}
          onChange={handleChange}
          fullWidth
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button onClick={handleSubmit} variant="contained">
          {isEdit ? '수정' : '추가'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryForm;
